import { useState } from 'react';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';

interface QualityMetadata {
    analysis_date?: string;
    algorithm_version?: string;
    baseline_window?: string;
    monitoring_window?: string;
    cloud_cover_baseline?: number;
    cloud_cover_monitoring?: number;
    valid_pixel_percentage?: number;
    data_source?: string;
    is_estimated?: boolean;
    confidence_level?: string;
    observation_count_baseline?: number;
    observation_count_monitoring?: number;
}

interface QualityMetadataPanelProps {
    metadata?: QualityMetadata | null;
    algorithmVersion?: string | null;
    isEstimated?: boolean;
    confidenceLevel?: string | null;
    analysisDate?: string | null;
    baselineYear?: number;
    monitoringYear?: number;
}

export default function QualityMetadataPanel({
    metadata,
    algorithmVersion,
    isEstimated,
    confidenceLevel,
    analysisDate,
    baselineYear,
    monitoringYear,
}: QualityMetadataPanelProps) {
    const [expanded, setExpanded] = useState(false);

    // Merge explicit props with metadata object
    const version = algorithmVersion || metadata?.algorithm_version || 'N/A';
    const estimated = isEstimated ?? metadata?.is_estimated ?? false;
    const confidence = confidenceLevel || metadata?.confidence_level || 'N/A';
    const date = analysisDate || metadata?.analysis_date || 'N/A';
    const source = metadata?.data_source || 'Sentinel-2 L2A + Sentinel-1 GRD';
    const baseline = metadata?.baseline_window || (baselineYear ? `Jan-Dec ${baselineYear}` : 'N/A');
    const monitoring = metadata?.monitoring_window || (monitoringYear ? `Jan-Dec ${monitoringYear}` : 'N/A');

    return (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Quality & Traceability
                    </span>
                </div>
                {expanded ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
            </button>

            {expanded && (
                <div className="p-3 space-y-2 text-sm">
                    <Row label="Analysis date" value={typeof date === 'string' ? date : new Date(date).toLocaleDateString()} />
                    <Row label="Algorithm version" value={version} />
                    <Row label="Data source" value={source} />
                    <Row label="Baseline window" value={baseline} />
                    <Row label="Monitoring window" value={monitoring} />
                    <Row label="Confidence" value={confidence} />
                    <Row label="Estimated" value={estimated ? 'Yes — subject to revision' : 'No'} highlight={estimated} />
                    {metadata?.cloud_cover_baseline != null && (
                        <Row label="Cloud cover (baseline)" value={`${metadata.cloud_cover_baseline.toFixed(1)}%`} />
                    )}
                    {metadata?.cloud_cover_monitoring != null && (
                        <Row label="Cloud cover (monitoring)" value={`${metadata.cloud_cover_monitoring.toFixed(1)}%`} />
                    )}
                    {metadata?.valid_pixel_percentage != null && (
                        <Row label="Valid pixels" value={`${metadata.valid_pixel_percentage.toFixed(1)}%`} />
                    )}
                </div>
            )}
        </div>
    );
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
    return (
        <div className="flex items-center justify-between py-1 border-b border-gray-100 dark:border-gray-700 last:border-0">
            <span className="text-gray-500 dark:text-gray-400">{label}</span>
            <span className={`font-medium ${highlight ? 'text-amber-600' : 'text-gray-900 dark:text-white'}`}>
                {value}
            </span>
        </div>
    );
}
